import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import { clockRange, dateStamp, stamp } from '@mds/shared/lib/dates';
import type { AdminCustomer, CustomerHistoryItem } from '@mds/shared/types/customer';

import { useSession } from '@/auth/use-session';
import CustomerTags from '@/components/customers/customer-tags';
import Chip from '@/components/ui/chip';
import Pager from '@/components/ui/pager';
import * as customers from '@/customers/customers.api';
import { bookingStatusLabel, bookingStatusTone } from '@/domain/bookings';
import { proposalStatusLabel, proposalStatusTone } from '@/domain/proposals';
import { formatCents } from '@/lib/money';

/** One titled block of the record. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 border-b border-hairline pb-2 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-mono text-sm tracking-wide text-ink-muted uppercase">{label}</dt>
      <dd className="m-0">{children}</dd>
    </>
  );
}

const HISTORY_PAGE_SIZE = 8;

const CHANNELS = [
  ['email', 'Email'],
  ['whatsapp', 'WhatsApp'],
  ['sms', 'SMS'],
] as const;

const EVENT_STATUS = {
  waitlisted: { label: 'Waitlisted', tone: 'border-warn-edge bg-warn-wash text-warn-text' },
  payment_pending: {
    label: 'Payment pending',
    tone: 'border-warn-edge bg-warn-wash text-warn-text',
  },
  confirmed: {
    label: 'Confirmed',
    tone: 'border-success-edge/35 bg-success-wash text-success-text',
  },
  cancelled: { label: 'Cancelled', tone: 'border-hairline bg-raised text-ink-muted' },
} as const;

function historyStatus(item: CustomerHistoryItem) {
  if (item.type === 'booking') {
    return { label: bookingStatusLabel(item.status), tone: bookingStatusTone(item.status) };
  }
  if (item.type === 'proposal') {
    return { label: proposalStatusLabel(item.status), tone: proposalStatusTone(item.status) };
  }
  return EVENT_STATUS[item.status];
}

function historyWhen(item: CustomerHistoryItem): string {
  const date =
    item.type === 'booking' && item.bookingEndDate !== item.scheduledDate
      ? `${dateStamp(item.scheduledDate)} – ${dateStamp(item.bookingEndDate)}`
      : dateStamp(item.scheduledDate);
  return item.startMinute !== null && item.endMinute !== null
    ? `${date} · ${clockRange(item.startMinute, item.endMinute)}`
    : date;
}

function historyMeasure(item: CustomerHistoryItem): string {
  if (item.type === 'event') {
    return `${item.seatCount} ${item.seatCount === 1 ? 'seat' : 'seats'} · ${formatCents(item.amountMinor ?? 0)}`;
  }
  if (item.type === 'proposal') {
    const quote = item.amountMinor === null ? 'No quote' : formatCents(item.amountMinor);
    return `${item.attendeeCount} ${item.attendeeCount === 1 ? 'guest' : 'guests'} · ${quote}`;
  }
  return formatCents(item.amountMinor ?? 0);
}

const historyTypeLabel = (type: CustomerHistoryItem['type']) =>
  type === 'booking' ? 'Booking' : type === 'event' ? 'Event' : 'Proposal';

/** Everything held on one customer, including their operational history. */
export default function CustomerRecord({
  customer,
  onSaved,
}: {
  customer: AdminCustomer;
  /** Called with the saved record after an inline edit, such as a tag change. */
  onSaved: (saved: AdminCustomer) => void;
}) {
  const { can } = useSession();
  const [historyPage, setHistoryPage] = useState(1);
  const prefs = customer.communicationPreferences;
  const optedIn = CHANNELS.filter(([key]) => prefs[key]);

  useEffect(() => setHistoryPage(1), [customer.id]);

  const history = useQuery({
    queryKey: ['customers', customer.id, 'history', historyPage],
    queryFn: () =>
      customers.getCustomerHistory(customer.id, {
        page: historyPage,
        limit: HISTORY_PAGE_SIZE,
      }),
  });
  const rows = history.data?.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <CustomerTags customer={customer} editable={can('crm.customers.manage')} onSaved={onSaved} />

      <Section title="Contact">
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
          <Line label="Ref">
            <span className="font-mono">{customer.reference}</span>
          </Line>
          <Line label="Email">
            <span className="break-all">{customer.email || 'Not given'}</span>
          </Line>
          <Line label="Phone">
            <span className="font-mono">{customer.phone || 'Not given'}</span>
          </Line>
          <Line label="Joined">
            <span className="font-mono">{dateStamp(customer.joinedAt)}</span>
          </Line>
        </dl>
      </Section>

      <Section title="Reachable on">
        {optedIn.length === 0 ? (
          <p className="m-0 text-base text-warn-text">
            Opted out of everything. Leave this one out of any send.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map(([key, label]) => (
              <Chip
                key={key}
                className={
                  prefs[key]
                    ? 'border border-brand-edge/40 bg-brand-wash text-brand-strong'
                    : 'text-ink-muted line-through'
                }
              >
                {label}
              </Chip>
            ))}
          </div>
        )}
        <p className="m-0 text-base text-ink-muted">
          Last changed {stamp(prefs.updatedAt)}
          {prefs.updatedByUserId ? ' by an administrator' : ''}.
        </p>
      </Section>

      <Section title="Interests">
        {customer.interests.length === 0 ? (
          <p className="m-0 text-base text-ink-muted">Nothing recorded yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {customer.interests.map((i) => (
              <Chip key={i}>{i}</Chip>
            ))}
          </div>
        )}
      </Section>

      <Section title="Internal notes">
        <p className="m-0 text-base whitespace-pre-line">
          {customer.internalNotes || <span className="text-ink-muted">Nothing noted.</span>}
        </p>
      </Section>

      <Section title="Customer history">
        {history.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading history…</p>
        ) : history.isError ? (
          <p className="m-0 text-base text-warn-text">
            {history.error instanceof ApiError
              ? history.error.message
              : 'The customer history could not be read.'}
          </p>
        ) : rows.length === 0 ? (
          <p className="m-0 text-base text-ink-muted">
            No bookings, event registrations, or proposals yet.
          </p>
        ) : (
          <>
            <dl className="m-0 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                ['Bookings', history.data.summary.bookingCount],
                ['Events', history.data.summary.eventRegistrationCount],
                ['Attended', history.data.summary.eventAttendanceCount],
                ['Proposals', history.data.summary.proposalCount],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-hairline bg-raised px-3 py-2">
                  <dt className="font-mono text-sm tracking-wide text-ink-muted uppercase">
                    {label}
                  </dt>
                  <dd className="m-0 text-lg font-semibold tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {rows.map((item) => {
                const status = historyStatus(item);
                return (
                  <li
                    key={`${item.type}-${item.id}`}
                    className="flex flex-col gap-2 rounded-xl border border-hairline px-4 py-3 text-base"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-semibold">{item.title}</span>
                        <span className="font-mono text-sm tracking-wide text-brand-text uppercase">
                          {historyTypeLabel(item.type)} · {item.reference}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {item.type === 'event' && item.attended && (
                          <Chip className="border border-brand-edge/35 bg-brand-wash text-brand-strong">
                            Attended
                          </Chip>
                        )}
                        <Chip className={'border ' + status.tone}>{status.label}</Chip>
                      </div>
                    </div>
                    <p className="m-0 text-md text-ink-muted">
                      {historyWhen(item)} · {item.detail}
                      {item.type === 'booking' && item.deskLabel ? ` · ${item.deskLabel}` : ''}
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-md">
                      <span className="font-semibold tabular-nums">{historyMeasure(item)}</span>
                      <span className="text-ink-muted">Added {stamp(item.occurredAt)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
            <Pager pagination={history.data.pagination} onPage={setHistoryPage} />
          </>
        )}
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          {can('reservations.read') && (
            <Link
              to={`/bookings?customerId=${customer.id}`}
              className="font-mono text-sm font-semibold tracking-wide text-brand-text uppercase underline decoration-2 underline-offset-4 hover:no-underline"
            >
              Bookings →
            </Link>
          )}
          {can('events.read') && (
            <Link
              to="/events/attendees"
              className="font-mono text-sm font-semibold tracking-wide text-brand-text uppercase underline decoration-2 underline-offset-4 hover:no-underline"
            >
              Event attendees →
            </Link>
          )}
          {can('reservations.read') && (
            <Link
              to="/proposals"
              className="font-mono text-sm font-semibold tracking-wide text-brand-text uppercase underline decoration-2 underline-offset-4 hover:no-underline"
            >
              Proposals →
            </Link>
          )}
        </div>
      </Section>
    </div>
  );
}
